import { useEffect, useMemo, useRef, useState } from 'react';
import { initialCrmEmailState } from 'components/Store';
import { useEmailState } from 'components/EmailStateProvider';
import deepEqual from 'components/Dashboard/helpers/deepEqual';
import useOutsideElement from 'components/Dashboard/utilities/useOutsideElement';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { applyEmailSignatureToBody } from './emailSignatureFunctions';
import useEmailComponentFunctions from 'components/Dashboard/Influence/CrmEmails/useEmailComponentFunctions';
import useGetInitialEmailAddress from './useGetInitialEmailAddress';
import useAddMetaTags from 'components/Dashboard/utilities/addMetaTags';
import pasteHandler from 'components/Dashboard/Influence/CrmEmails/pasteHandler';
import useGeneralApiCall from 'components/Dashboard/apiCalls/useGeneralApiCall';
import useCreateSummaryFromWritingAssistant from 'components/Dashboard/UserContent/useCreateSummaryFromWritingAssistant';
import dynamicTextImage from 'components/Dashboard/Influence/CrmEmails/TinyEditor/dynamicTextImage';
import normalizeCrmEmailModel from './normalizeCrmEmailModel';
import { resolveEmailRouteState } from './emailRouteState';
import { buildEmailAttachmentHtml } from 'components/Dashboard/Influence/CrmEmails/buildEmailAttachmentHtml';

const mapSendRecordsToRecipients = (records = []) =>
  records.map((record) => ({ id: record.crmContactId, email: record.emailAddress }));

const normalizeEmailComparable = ({ emailDetails, compareModel }) => ({
  from: compareModel?.senderEmailAddressId,
  subject: compareModel?.subject,
  body: compareModel?.body?.replace(/(?:\r\n|\r|\n)/g, ''),
  emailDomainId: emailDetails.status ? compareModel?.emailDomainId : 1,
});

const normalizeRecipientsComparable = (recipients = []) =>
  [...recipients]
    .map((recipient) => ({
      id: recipient?.id ?? null,
      email: recipient?.email ?? '',
    }))
    .sort((a, b) => {
      const idA = a.id ?? Number.MAX_SAFE_INTEGER;
      const idB = b.id ?? Number.MAX_SAFE_INTEGER;
      if (idA !== idB) return idA - idB;
      return String(a.email).localeCompare(String(b.email));
    });

const canLeaveEmailPage = ({ currentEmailDetails, initialEmail, initialToList, emailSelectedContacts }) => {
  if (currentEmailDetails.status === 'Sent' || currentEmailDetails.status === 'PartiallySent') {
    return true;
  }

  if (currentEmailDetails?.cloned) {
    return false;
  }

  const initialComparable = normalizeEmailComparable({ emailDetails: currentEmailDetails, compareModel: initialEmail });
  const currentComparable = normalizeEmailComparable({
    emailDetails: currentEmailDetails,
    compareModel: currentEmailDetails,
  });

  return (
    deepEqual(initialComparable, currentComparable) &&
    deepEqual(normalizeRecipientsComparable(initialToList), normalizeRecipientsComparable(emailSelectedContacts))
  );
};

const useEmailComponentEffects = ({ emailId, callGroupsList, dispatch, listDomainsVerified, emailAddresses }) => {
  const { emailState, setEmailState } = useEmailState();
  const { emailStateAfterSave, currentEmailDetails, emailSelectedContacts } = emailState;
  const { getEmail } = useEmailComponentFunctions();
  const { getInitialSenderEmailAddress } = useGetInitialEmailAddress();
  const { addMetaTags } = useAddMetaTags();
  const { generalApiCall } = useGeneralApiCall();
  const { convertToMarkdown } = useCreateSummaryFromWritingAssistant();
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const crmEmailId = emailId ?? params?.crmEmailId;
  const routeState = resolveEmailRouteState({ location, defaultTab: 'Email' });

  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingWritingAssistant, setIsLoadingWritingAssistant] = useState(false);
  const [editorOnLoad, setEditorOnLoad] = useState(true);
  const [showLeavePopUp, setShowLeavePopUp] = useState(null);
  const [initialEmail, setInitialEmail] = useState(emailStateAfterSave);
  const [initialToList, setInitialToList] = useState([]);

  const autofocusFieldFromUrl = useRef(null);
  const emailContainerRef = useRef(null);
  const hasDisplayedEmailContentRef = useRef(false);

  const resetEmailStateAfterSave = () => {
    setEmailState('emailStateAfterSave', initialCrmEmailState());
  };

  const syncInitialEmailSnapshot = (emailModel) => {
    setInitialEmail(emailModel);
    setInitialToList(mapSendRecordsToRecipients(emailModel?.sendRecords));
  };

  const handleClone = (content) => {
    if (!!content) {
      const {
        body,
        subject,
        title,
        sendRecords,
        senderDisplayName,
        senderEmailAddressId,
        senderEmailAddress,
        sendRecordCount,
      } = content;
      let email = {
        body: pasteHandler(body),
        subject,
        title,
        senderDisplayName,
        senderEmailAddressId,
        senderEmailAddress,
        sendRecordCount,
        sendRecords: routeState.notIncludeRecipient ? [] : sendRecords,
        cloned: true,
      };
      return email;
    }
  };

  const createEmailFromWritingAssistant = async (source) => {
    setIsLoadingWritingAssistant(true);
    const pathname = `/api/writing-assistant-generated-content/${routeState.writingAssistantId}`;
    const method = 'get';
    const results = await generalApiCall({
      pathname,
      method,
      needsAuthentication: true,
      requestSource: source,
      notShowErrorMessage: true,
    });
    if (results) {
      let bodyHasSubject = results?.body?.match(/Subject(.*?)\n\n/g);
      let initialBody = !!bodyHasSubject ? results?.body?.replaceAll(/Subject(.*?)\n\n/g, '') : results?.body;
      let bodyContainsDearSentence = initialBody?.match(/Dear(.*?)\n\n/g);
      let resultsBody = bodyContainsDearSentence
        ? initialBody?.replaceAll(
            /Dear(.*?)\n\n/g,
            `<p style="font-family: Arial, Helvetica, sans-serif; font-size: 15px; margin: 0; color: #00122b;">Dear&nbsp;${dynamicTextImage(
              'salutation'
            )},&nbsp;</p>`
          )
        : initialBody;

      let emailTitle = bodyHasSubject ? bodyHasSubject[0]?.replace('Subject: ', '') : results?.title;
      let bodyCall = await convertToMarkdown({ markdown: resultsBody, source });
      let body = bodyCall?.html;
      body = body?.replaceAll(
        '<p>',
        `<p style="font-family: Arial, Helvetica, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol'; font-size: 15px; margin: 0; color: #00122b;">`
      );
      body = body?.replaceAll('</p>', '</p><br/>');
      body = applyEmailSignatureToBody({
        emailAddressItem: getInitialSenderEmailAddress(false, emailAddresses),
        emailBody: body,
      });
      let newEmailState = {
        ...initialCrmEmailState(),
        subject: emailTitle,
        title: emailTitle,
        body: `${body}`,
        updateFromAiAssistant: true,
      };
      navigate({}, { replace: true });
      setEmailState('currentEmailDetails', newEmailState);
      setIsLoadingWritingAssistant(false);
    }
  };

  const refreshEmailData = async (source, updateMailMergeData = false) => {
    try {
      // `emailAddresses` and domain verification can resolve after the first render, which
      // re-triggers this fetch. Only show the loading skeleton on the initial load.
      if (!updateMailMergeData && !hasDisplayedEmailContentRef.current) {
        setIsLoading(true);
      }
      if (!listDomainsVerified.domainsProccessing) {
        let result = await getEmail({ emailId: routeState.cloneEmailId ?? emailId, source });
        let crmEmailModel = updateMailMergeData
          ? {
              ...currentEmailDetails,
              mailMergeFields: result?.mailMergeFields,
              hasMailMergeData: result?.hasMailMergeData,
            }
          : result;
        if (result?.status === 403 || result?.status === 404) {
          navigate('/people/emails');
        }
        if (!!crmEmailModel) {
          crmEmailModel = normalizeCrmEmailModel({
            crmEmailModel,
            getInitialSenderEmailAddress,
            emailAddresses,
          });

          syncInitialEmailSnapshot(crmEmailModel);
          if (
            (crmEmailModel.status === 'Sent' || crmEmailModel.status === 'PartiallySent') &&
            !routeState.cloneEmailId
          ) {
            if (location.pathname.startsWith('/people/emails')) {
              navigate(`/people/email-performance/${crmEmailModel.crmEmailId}`);
            }
          }
          setEmailState('currentEmailDetails', routeState.cloneEmailId ? handleClone(crmEmailModel) : crmEmailModel);
          let title = `${!!crmEmailModel?.subject?.length ? `${crmEmailModel?.subject} - ` : ''}${
            crmEmailModel?.status ? crmEmailModel?.status : 'Email'
          } - PolicyMogul`;
          addMetaTags({ title, hash: null });
          setIsLoading(false);
          hasDisplayedEmailContentRef.current = true;
        } else {
          let title = `Create new email - Influence - PolicyMogul`;
          addMetaTags({ title, hash: null });
          let emailModelWithSignature = initialCrmEmailState();
          emailModelWithSignature.body = applyEmailSignatureToBody({
            emailAddressItem: getInitialSenderEmailAddress(null, emailAddresses),
            emailBody: emailModelWithSignature.body,
          });
          if (routeState.prepopulatedAttachment) {
            emailModelWithSignature.body =
              emailModelWithSignature.body + buildEmailAttachmentHtml([routeState.prepopulatedAttachment]);
          }
          setInitialEmail(emailModelWithSignature);
          setEmailState('currentEmailDetails', emailModelWithSignature);
          if (routeState.writingAssistantId) {
            let result = await createEmailFromWritingAssistant(source);
            if (result) {
              setIsLoading(false);
            }
          }
        }
        if (location.pathname === '/people/emails/create') {
          setIsLoading(false);
          hasDisplayedEmailContentRef.current = true;
        }
      }
    } catch (error) {}
  };

  const handleDiscardNavigation = () => {
    if (!showLeavePopUp) return;
    resetEmailStateAfterSave();
    setShowLeavePopUp(null);
    navigate(showLeavePopUp);
  };

  const leaveEmailPageCondition = useMemo(
    () =>
      canLeaveEmailPage({
        currentEmailDetails,
        initialEmail,
        initialToList,
        emailSelectedContacts,
      }),
    [currentEmailDetails, initialEmail, initialToList, emailSelectedContacts]
  );

  useEffect(() => {
    autofocusFieldFromUrl.current = routeState.autofocusFieldFromUrl;
  }, [routeState.autofocusFieldFromUrl]);

  useEffect(() => {
    const source = new AbortController();
    callGroupsList.current(source);
    return () => {
      source.abort('Email cancelled by the user');
      dispatch({
        type: 'MODIFY_SECTION',
        parameter: 'newContactFromSelect',
        value: null,
      });
      setEmailState('currentEmailDetails', initialCrmEmailState());
      setEmailState('emailSelectedContacts', []);
    };
  }, []);

  useEffect(() => {
    syncInitialEmailSnapshot(emailStateAfterSave);
  }, [emailStateAfterSave]);

  useOutsideElement(
    emailContainerRef,
    (target, event) => {
      const anchorElement = target?.closest?.('a');
      const targetHasLink = anchorElement?.href ?? target?.href;
      const preparedLink = targetHasLink?.replace(window.location.origin, '');
      if (preparedLink && !leaveEmailPageCondition) {
        // Stop immediate route transition so discard modal can open first.
        event?.preventDefault?.();
        event?.stopPropagation?.();
        setShowLeavePopUp(preparedLink);
      }
    },
    [leaveEmailPageCondition],
    'click',
    true
  );

  useEffect(() => {
    if (!!listDomainsVerified.verifiedDomains.length && currentEmailDetails.emailDomainId === null) {
      setEmailState('currentEmailDetails', {
        ...currentEmailDetails,
        emailDomainId: listDomainsVerified.verifiedDomains[0].id,
      });
    }

    if (
      !listDomainsVerified.verifiedDomains.length &&
      !listDomainsVerified.verifiedEmailAddresses.length &&
      !listDomainsVerified.domainsProccessing &&
      !isLoading &&
      !currentEmailDetails.crmEmailId &&
      !isLoadingWritingAssistant &&
      !routeState.writingAssistantId
    ) {
      navigate('/people/emails', { replace: true });
    }
  }, [listDomainsVerified, isLoading, isLoadingWritingAssistant, routeState.writingAssistantId]);

  useEffect(() => {
    hasDisplayedEmailContentRef.current = false;
  }, [crmEmailId]);

  useEffect(() => {
    const source = new AbortController();
    try {
      refreshEmailData(source);
    } catch {}
    return () => {
      source.abort('Email cancelled by the user');
    };
  }, [crmEmailId, emailAddresses, listDomainsVerified.domainsProccessing]);

  return {
    isLoading,
    setIsLoading,
    isLoadingWritingAssistant,
    setIsLoadingWritingAssistant,
    editorOnLoad,
    setEditorOnLoad,
    handleDiscardNavigation,
    showLeavePopUp,
    setShowLeavePopUp,
    initialEmail,
    setInitialEmail,
    syncInitialEmailSnapshot,
    autofocusFieldFromUrl,
    emailContainerRef,
    refreshEmailData,
  };
};

export default useEmailComponentEffects;
